import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Table from 'components/Table'
import { selectActiveStudents } from 'modules/students/ducks/selectors'
import StudentsTableRow from 'modules/students/components/StudentsTableRow'
import { isNilOrEmpty, isNotNil, isNotNilOrEmpty } from 'utils/ramda'
import styled from 'styled-components'
import Input from 'components/Input'
import SearchIcon from '@mui/icons-material/Search'
import { isNil } from 'ramda'
import Checkbox from 'components/Checkbox'
import Button from 'components/Button'
import Modal from 'components/Modal'

const StudentsTable = ({ studentsList, actionCallback, showSearch }) => {
  const activeStudents = useSelector(selectActiveStudents)
  const isMainStudentsView = isNil(studentsList)
  const list = isNotNil(studentsList)
    ? studentsList
    : activeStudents.filter(student => !student.isNew)
  const [filtered, setFiltered] = useState(list)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStudents, setSelectedStudents] = useState([])
  const [isPhoneNumbersModalOpen, setIsPhoneNumbersModalOpen] = useState(false)

  useEffect(() => {
    const query = searchQuery.toLowerCase().trim()
    setFiltered(list.filter(student => {
      const name = (student.name || '').toLowerCase()
      const email = (student.email || '').toLowerCase()
      return name.includes(query) || email.includes(query)
    }))
  }, [searchQuery, list])

  const selectableStudents = filtered.filter(student => isNotNilOrEmpty(student.phoneNumber))

  const handleSelectAll = (_, checked) => {
    if (checked) {
      setSelectedStudents(selectableStudents.map(student => student.id))
    } else {
      setSelectedStudents([])
    }
  }

  const handleSelectStudent = (studentId, checked) => {
    if (checked) {
      setSelectedStudents(prev => [...prev, studentId])
    } else {
      setSelectedStudents(prev => prev.filter(id => id !== studentId))
    }
  }

  const handleClearSelected = () => setSelectedStudents([])

  const handlePhoneNumbersModalOpen = () => setIsPhoneNumbersModalOpen(true)
  const handlePhoneNumbersModalClose = () => setIsPhoneNumbersModalOpen(false)

  const isAllSelected = selectableStudents.length > 0 &&
    selectableStudents.every(student => selectedStudents.includes(student.id))

  return (
    <>
      {
        (showSearch || isMainStudentsView) && (
          <FiltersRow>
            <InputWrapper>
              <Input
                name='search'
                placeholder='Szukaj...'
                onChange={(_, value) => setSearchQuery(value)}
                value={searchQuery}
                startAdornment={<SearchIcon />}
              />
            </InputWrapper>
            {isMainStudentsView && (
              <ButtonsContainer>
                <Button
                  color='primary-outline'
                  disabled={isNilOrEmpty(selectedStudents)}
                  onClick={handleClearSelected}
                >
                  Wyczyść zaznaczenie
                </Button>
                <Button
                  color='primary-outline'
                  disabled={isNilOrEmpty(selectedStudents)}
                  onClick={handlePhoneNumbersModalOpen}
                >
                  Pokaż numery telefonów
                </Button>
              </ButtonsContainer>
            )}
          </FiltersRow>
        )
      }
      <Table>
        <thead>
        <tr>
          {isMainStudentsView && (
            <th>
              <CheckboxWrapper>
                <Checkbox
                  value={isAllSelected}
                  onChange={handleSelectAll}
                  disabled={isNilOrEmpty(selectableStudents)}
                />
              </CheckboxWrapper>
            </th>
          )}
          <th>Lp.</th>
          <th>Nazwa</th>
          <th>Typ lekcji</th>
          <th>Przedmiot</th>
          <th>Nauczyciel</th>
          <th>Czas</th>
          <th>Stawka ucz.</th>
          <th>Stawka nau.</th>
          <th>Email</th>
          <th>Tel.</th>
          <th />
        </tr>
        </thead>
        <tbody>
        {filtered.map((student, index) => (
          <StudentsTableRow
            actionCallback={actionCallback}
            isTeacherView={isNotNil(studentsList)}
            showSelection={isMainStudentsView}
            isSelected={selectedStudents.includes(student.id)}
            onSelect={handleSelectStudent}
            order={index + 1}
            student={student}
            key={`student-${student.id}`}
          />
        ))}
        </tbody>
      </Table>
      {isNilOrEmpty(filtered) && (
        <EmptyState>
          Brak uczniów do wyświetlenia
        </EmptyState>
      )}
      {isMainStudentsView && (
        <Modal
          title='Numery zaznaczonych uczniów'
          open={isPhoneNumbersModalOpen}
          onClose={handlePhoneNumbersModalClose}
        >
          {list
            .filter(student => selectedStudents.includes(student.id) && isNotNilOrEmpty(student.phoneNumber))
            .map(student => student.phoneNumber)
            .join('; ')}
        </Modal>
      )}
    </>
  )
}

export default StudentsTable

const EmptyState = styled.div`
  width: 100%;
  height: 70px;
  display: flex;
  align-items: center;
`

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`

const InputWrapper = styled.div`
  display: flex;

  div {
    max-width: 300px;
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 10px;

  & > button {
    min-width: 250px;
  }
`

const CheckboxWrapper = styled.div`
  display: flex;
  justify-content: center;
`
